export interface DepartmentLike {
  id: string;
  name: string;
  parentDepartmentId?: string;
  isActive?: boolean;
}

export interface HierarchicalDepartmentOption {
  id: string;
  name: string;
  depth: number;
  hasChildren: boolean;
}

const DEPARTMENT_INDENT = '\u00A0\u00A0';

export const formatDepartmentOptionLabel = (name: string, depth: number): string => {
  if (depth <= 0) {
    return name;
  }

  return `${DEPARTMENT_INDENT.repeat(depth)}${name}`;
};

export const buildDepartmentLabelMap = (departments: DepartmentLike[]): Map<string, string> => {
  const byId = new Map(departments.map(item => [item.id, item]));
  const labels = new Map<string, string>();

  const buildLabel = (departmentId: string, visited = new Set<string>()): string => {
    const cached = labels.get(departmentId);
    if (cached) {
      return cached;
    }

    const current = byId.get(departmentId);
    if (!current) {
      return '';
    }

    if (visited.has(departmentId)) {
      return current.name;
    }

    visited.add(departmentId);
    const parentLabel = current.parentDepartmentId ? buildLabel(current.parentDepartmentId, visited) : '';
    const label = parentLabel ? `${parentLabel} / ${current.name}` : current.name;
    labels.set(departmentId, label);
    return label;
  };

  departments.forEach(department => {
    buildLabel(department.id);
  });

  return labels;
};

export const buildHierarchicalDepartmentOptions = (
  departments: DepartmentLike[],
  { activeOnly = true }: { activeOnly?: boolean } = {},
): HierarchicalDepartmentOption[] => {
  const items = activeOnly ? departments.filter(item => item.isActive !== false) : departments;
  const knownIds = new Set(items.map(item => item.id));
  const childrenByParent = new Map<string | null, DepartmentLike[]>();

  for (const department of items) {
    const parentId = department.parentDepartmentId && knownIds.has(department.parentDepartmentId)
      ? department.parentDepartmentId
      : null;

    const siblings = childrenByParent.get(parentId) || [];
    siblings.push(department);
    childrenByParent.set(parentId, siblings);
  }

  Array.from(childrenByParent.values()).forEach(siblings => {
    siblings.sort((left, right) => left.name.localeCompare(right.name));
  });

  const result: HierarchicalDepartmentOption[] = [];

  const walk = (parentId: string | null, depth: number, visited: Set<string>) => {
    const children = childrenByParent.get(parentId) || [];

    for (const department of children) {
      if (visited.has(department.id)) {
        continue;
      }

      visited.add(department.id);
      const children = childrenByParent.get(department.id) || [];
      result.push({
        id: department.id,
        name: department.name,
        depth,
        hasChildren: children.length > 0,
      });
      walk(department.id, depth + 1, visited);
    }
  };

  walk(null, 0, new Set());
  return result;
};
